/**
 * Planning-circle / move-line / dest colours authored on `MapDocument.look.aimChrome`.
 * Circle stroke alphas stay in `planningCircle.ts`.
 */

import { clampRgba, cloneRgba, isRgba, rgbaFromRgb24, type Rgba } from '../color.ts';

export type AimChrome = {
  movement: Rgba;
  prediction: Rgba;
  moveLine: Rgba;
  dest: Rgba;
};

export const DEFAULT_AIM_CHROME: AimChrome = {
  movement: rgbaFromRgb24(0x2fbe6a, 0.2),
  prediction: rgbaFromRgb24(0xf4d35e, 0.08),
  moveLine: rgbaFromRgb24(0xe24a4a, 0.9),
  dest: rgbaFromRgb24(0xe24a4a, 0.95),
};

export function cloneAimChrome(value: AimChrome): AimChrome {
  return {
    movement: cloneRgba(value.movement),
    prediction: cloneRgba(value.prediction),
    moveLine: cloneRgba(value.moveLine),
    dest: cloneRgba(value.dest),
  };
}

function asRgba(value: unknown, fallback: Rgba): Rgba {
  return isRgba(value) ? clampRgba(value) : cloneRgba(fallback);
}

export function migrateAimChrome(value: unknown): AimChrome {
  const factory = DEFAULT_AIM_CHROME;

  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return cloneAimChrome(factory);
  }

  const record = value as Record<string, unknown>;

  return {
    movement: asRgba(record.movement, factory.movement),
    prediction: asRgba(record.prediction, factory.prediction),
    moveLine: asRgba(record.moveLine, factory.moveLine),
    dest: asRgba(record.dest, factory.dest),
  };
}
